// frontend/src/auth/AuthScreens.jsx  (FIXED)
// FIXES: [C4] DPDP Act 2023 consent gate, privacy policy link, consent audit logging
// FIXES: [C2] MFA setup prompt after first login
// FIXES: [C3] No raw error.message shown to users for unexpected errors

import { useState, useEffect, useRef } from "react";
import {
  signUp, confirmSignUp, signIn, signOut,
  resendSignUpCode,
  resetPassword, confirmResetPassword, getCurrentUser,
  setUpTOTP, verifyTOTPSetup, updateMFAPreference,
  updateUserAttribute,
} from "aws-amplify/auth";

const C = {
  bg: "#f4f2ee", surface: "#f3f2f0", card: "#ffffff", border: "rgba(0,0,0,0.08)",
  accent: "#1565d8", red: "#cc1016", gold: "#915907",
  text: "rgba(0,0,0,0.9)", muted: "rgba(0,0,0,0.6)", success: "#057642",
};

// Current privacy policy version — bump this when policy changes to re-collect consent
const CONSENT_VERSION = "v1.0-2026-05";

const PRIVACY_POLICY_URL = "/privacy-policy.html";  // Host this on your domain

// FIX [C4]: Log consent to backend (categories Lambda handles it via /consent endpoint)
const recordConsent = async (action) => {
  try {
    const API = process.env.REACT_APP_API_ENDPOINT;
    // This endpoint doesn't need auth (it's for recording consent at signup time)
    // It only writes to the consent-audit table — no read access
    await fetch(`${API}/consent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action,                        // "given" or "withdrawn"
        consentVersion: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent.slice(0, 200),
      }),
    });
  } catch {
    // Non-blocking — consent is also stored in Cognito user attributes
    console.warn("Consent API unavailable, recorded in Cognito attributes only");
  }
};

// ── Reusable sub-components ───────────────────────────────────────────────
const Field = ({ label, type = "text", value, onChange, placeholder }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label style={{ color: C.muted, fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>{label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", color: C.text, fontSize: 15, fontFamily: "inherit", outline: "none" }} />
  </div>
);

const Btn = ({ onClick, children, loading, variant = "primary", disabled }) => (
  <button onClick={onClick} disabled={loading || disabled}
    style={{
      width: "100%",
      padding: "10px 16px",
      borderRadius: 24,
      border: "none",
      background: variant === "primary" ? C.accent : variant === "gold" ? C.gold : "transparent",
      color: (variant === "primary" || variant === "gold") ? "#ffffff" : C.muted,
      fontWeight: 600,
      fontSize: 14,
      cursor: (loading || disabled) ? "not-allowed" : "pointer",
      opacity: (loading || disabled) ? 0.6 : 1,
      ...(variant === "ghost" ? { border: `1px solid ${C.border}`, color: C.text } : {}),
    }}>
    {loading ? "Please wait..." : children}
  </button>
);

const ErrBox = ({ msg }) => msg ? (
  <div style={{ color: C.red, fontSize: 13, background: C.red + "11", padding: "10px 14px", borderRadius: 8, border: `1px solid ${C.red}33` }}>{msg}</div>
) : null;

// LinkedIn-style auth page wrapper: logo top-left, centered card with title only.
const LogoMark = () => (
  <svg width="32" height="32" viewBox="0 0 100 100" aria-hidden="true">
    <rect width="100" height="100" rx="8" fill="#1565d8"/>
    <g fill="none" stroke="#ffffff" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M 40 14 C 37 18, 34 24, 33 32 L 28 76 C 27 84, 33 88, 44 82"/>
      <path d="M 66 30 C 70 10, 56 6, 52 14 C 48 20, 50 28, 54 30 L 70 76 C 72 84, 80 87, 76 82"/>
      <path d="M 16 44 C 24 41, 36 41, 44 44"/>
      <path d="M 46 52 C 56 49, 70 49, 80 52"/>
    </g>
  </svg>
);

const Card = ({ children, title, subtitle }) => (
  <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column" }}>
    {/* Top header — logo at top-left, LinkedIn style */}
    <div style={{ background: C.card, borderBottom: `1px solid ${C.border}`, padding: "14px 24px" }}>
      <div style={{ maxWidth: 1128, margin: "0 auto", display: "flex", alignItems: "center", gap: 10 }}>
        <LogoMark />
        <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.3px", lineHeight: 1 }}>
          <span style={{ color: C.text }}>Track</span><span style={{ fontWeight: 400, color: C.accent }}>Fin</span>
        </div>
      </div>
    </div>

    {/* Centered form area */}
    <div style={{ flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "32px 16px 48px" }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: "28px 32px", width: "100%", maxWidth: 400, boxShadow: "0 4px 12px rgba(0,0,0,.08)", marginTop: 24 }}>
        {title && <div style={{ fontSize: 28, fontWeight: 700, color: C.text, letterSpacing: "-0.5px", marginBottom: subtitle ? 6 : 20 }}>{title}</div>}
        {subtitle && <div style={{ color: C.muted, fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>{subtitle}</div>}
        {children}
      </div>
    </div>
  </div>
);

// ── Signup Screen (with DPDP consent) ─────────────────────────────────────
export function SignupScreen({ onSuccess, onLogin, onResendCode }) {
  const [name, setName]             = useState("");
  const [email, setEmail]           = useState("");
  const [password, setPassword]     = useState("");
  const [consentGiven, setConsent]  = useState(false);
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [policyExpanded, setPolicyExpanded] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileSkipped, setTurnstileSkipped] = useState(false);
  const turnstileRef  = useRef(null);
  const widgetIdRef   = useRef(null);

  useEffect(() => {
    let timeoutId;
    const renderWidget = () => {
      if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
        try {
          widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
            sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
            callback: (token) => setTurnstileToken(token),
            "expired-callback": () => setTurnstileToken(null),
            "error-callback": () => { console.warn("Turnstile error, allowing signup without check"); setTurnstileSkipped(true); },
            appearance: "interaction-only",
          });
        } catch (e) {
          console.warn("Turnstile render failed, skipping check:", e);
          setTurnstileSkipped(true);
        }
      }
    };
    if (window.turnstile) { renderWidget(); }
    else {
      const id = setInterval(() => { if (window.turnstile) { clearInterval(id); renderWidget(); } }, 100);
      // Fallback: if Turnstile script never loads in 8s, allow signup anyway
      timeoutId = setTimeout(() => {
        if (!widgetIdRef.current) {
          clearInterval(id);
          console.warn("Turnstile script load timeout, allowing signup without check");
          setTurnstileSkipped(true);
        }
      }, 8000);
      return () => { clearInterval(id); if (timeoutId) clearTimeout(timeoutId); };
    }
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
      }
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const handleResendCode = async () => {
    setError(""); setLoading(true);
    try {
      await resendSignUpCode({ username: email });
      onResendCode?.(email);
    } catch (e) {
      const msg = String(e?.message || "");
      if (/already confirmed/i.test(msg)) {
        setError("This email is already verified. Click \"Sign in with this email\" below.");
        // keep showResend=true so the user can navigate to login
      } else {
        setError(mapCognitoError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    setError(""); setShowResend(false);
    if (!consentGiven) {
      setError("You must accept the Privacy Policy and consent to data processing to continue.");
      return;
    }
    if (!turnstileToken && !turnstileSkipped) {
      setError("Security check in progress — please wait a moment and try again.");
      return;
    }
    setLoading(true);
    try {
      // Verify bot check with our backend (skip if Turnstile didn't load)
      if (turnstileToken) {
        const API = process.env.REACT_APP_API_ENDPOINT;
        const verifyRes = await fetch(`${API}/verify-human`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: turnstileToken }),
        });
        const verifyData = await verifyRes.json();
        if (!verifyData.success) {
          setError(verifyData.error || "Security check failed. Please refresh and try again.");
          if (widgetIdRef.current && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
            setTurnstileToken(null);
          }
          setLoading(false);
          return;
        }
      }
    try {
      const consentTimestamp = new Date().toISOString();
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name,
            // FIX [C4]: Store consent in Cognito user attributes for audit
            "custom:consent_given":  consentTimestamp,
            "custom:consent_version": CONSENT_VERSION,
          },
        },
      });
      await recordConsent("given");
      onSuccess(email);
    } catch (e) {
      const code = e?.name || e?.code || "";
      if (code === "UsernameExistsException") {
        setError("An account with this email already exists. If you didn't finish verifying, you can resend the code below.");
        setShowResend(true);
      } else {
        setError(mapCognitoError(e));
      }
    }
    } catch (e) {
      setError("Security check failed. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card subtitle="Start your financial journey." title="Join TrackFin">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Field label="Full Name" value={name} onChange={setName} placeholder="Raj Kumar" />
        <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@email.com" />
        <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="Min 8 chars, uppercase, number, symbol" />

        {/* DPDP Act 2023 consent gate — compact by default, "Read more" expands details */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
          <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}>
            <input type="checkbox" checked={consentGiven} onChange={e => setConsent(e.target.checked)}
              style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, accentColor: C.accent }} />
            <span style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
              I agree to the{" "}
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>Privacy Policy</a>
              {" "}and consent to TrackFin processing my financial data.
            </span>
          </label>
          <button type="button"
            onClick={(e) => { e.preventDefault(); setPolicyExpanded(p => !p); }}
            style={{ background: "none", border: "none", color: C.accent, fontSize: 12, cursor: "pointer", padding: "6px 0 0 28px", fontWeight: 600 }}>
            {policyExpanded ? "▴ Hide details" : "▾ Read more"}
          </button>
          {policyExpanded && (
            <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 44, display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
              <li>Raw transaction data is automatically deleted after 45 days</li>
              <li>Anonymised category summaries are retained for financial trend analysis</li>
              <li>I can request deletion of all my data at any time</li>
              <li>My data is never sold or shared with third parties</li>
            </ul>
          )}
        </div>

        <div ref={turnstileRef} />
        <ErrBox msg={error} />
        {showResend ? (
          <>
            <Btn onClick={handleResendCode} loading={loading}>📧 Resend Verification Code</Btn>
            <button onClick={onLogin}
              style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", padding: 0, marginTop: 8 }}>
              Or sign in with this email →
            </button>
          </>
        ) : (
          <Btn onClick={handleSignup} loading={loading} disabled={!consentGiven}>Create Account</Btn>
        )}

        <div style={{ textAlign: "center" }}>
          <button onClick={onLogin} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer" }}>
            Already have an account? <span style={{ color: C.accent }}>Sign in</span>
          </button>
        </div>
      </div>
    </Card>
  );
}

// ── Login Screen ──────────────────────────────────────────────────────────
export function LoginScreen({ onSuccess, onSignup, onForgot, onMfaSetup, onResendCode }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);

  const handleResend = async () => {
    if (!email) { setError("Enter your email above first."); return; }
    setError(""); setLoading(true);
    try {
      await resendSignUpCode({ username: email });
      onResendCode?.(email);
    } catch (e) {
      const msg = String(e?.message || "");
      if (/already confirmed/i.test(msg)) {
        setError("This email is already verified. Just enter your password and sign in.");
        setNeedsConfirmation(false);
      } else if (/User does not exist/i.test(msg)) {
        setError("No account found with this email. Create an account instead?");
        setNeedsConfirmation(false);
      } else {
        setError(mapCognitoError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setError(""); setNeedsConfirmation(false); setLoading(true);
    try {
      const result = await signIn({ username: email, password });
      // FIX [C2]: If MFA setup is required (first login), redirect to MFA setup screen
      if (result.nextStep?.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
        onMfaSetup(result);
      } else if (result.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
        // User exists but email is not verified yet
        setNeedsConfirmation(true);
        setError("Your email isn't verified yet. Resend the code below to complete signup.");
      } else {
        onSuccess();
      }
    } catch (e) {
      const code = e?.name || e?.code || "";
      if (code === "UserNotConfirmedException") {
        setNeedsConfirmation(true);
        setError("Your email isn't verified yet. Resend the code below to complete signup.");
      } else {
        setError(mapCognitoError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card subtitle="Track your money, plan your peace." title="Sign in">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@email.com" />
        <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" />
        <ErrBox msg={error} />
        {needsConfirmation ? (
          <>
            <Btn onClick={handleResend} loading={loading}>📧 Resend Verification Code</Btn>
            <button onClick={() => setNeedsConfirmation(false)}
              style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", padding: 0 }}>
              ← Try a different account
            </button>
          </>
        ) : (
          <Btn onClick={handleLogin} loading={loading}>Sign In</Btn>
        )}
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <button onClick={onForgot} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer" }}>Forgot password?</button>
          <button onClick={onSignup} style={{ background: "none", border: "none", color: C.accent, fontSize: 13, cursor: "pointer", fontWeight: 600 }}>Create account →</button>
        </div>
      </div>
    </Card>
  );
}

// ── Email Verification Screen ─────────────────────────────────────────────
export function VerifyScreen({ email, onSuccess }) {
  const [code, setCode]     = useState("");
  const [error, setError]   = useState("");
  const [info, setInfo]     = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [verified, setVerified]   = useState(false);
  const [countdown, setCountdown] = useState(10);

  // After verification succeeds, count down 10 → 0 then auto-route to login
  useEffect(() => {
    if (!verified) return;
    if (countdown <= 0) { onSuccess(); return; }
    const id = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [verified, countdown, onSuccess]);

  const handleVerify = async () => {
    setError(""); setInfo(""); setLoading(true);
    try {
      await confirmSignUp({ username: email, confirmationCode: code.trim() });
      setVerified(true);
    } catch (e) {
      setError(mapCognitoError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError(""); setInfo(""); setResending(true);
    try {
      await resendSignUpCode({ username: email });
      setInfo("A fresh code has been sent. Check your inbox.");
    } catch (e) {
      setError(mapCognitoError(e));
    } finally {
      setResending(false);
    }
  };

  if (verified) {
    return (
      <Card subtitle="All set" title="Email verified">
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: C.success + "22", border: `2px solid ${C.success}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36 }}>
            ✓
          </div>
          <div style={{ textAlign: "center", color: C.text, fontSize: 15, fontWeight: 600 }}>
            Your email <strong style={{ color: C.accent }}>{email}</strong> has been verified successfully.
          </div>
          <div style={{ textAlign: "center", color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
            Please sign in with your password to continue.
          </div>
          <Btn onClick={onSuccess}>OK · Go to Sign In</Btn>
          <div style={{ fontSize: 12, color: C.muted }}>
            Redirecting in <strong style={{ color: C.accent }}>{countdown}</strong> second{countdown === 1 ? "" : "s"}…
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card subtitle="Check your email" title="Verify your account">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ color: C.muted, fontSize: 14, lineHeight: 1.6 }}>
          A 6-digit code was sent to <strong style={{ color: C.text }}>{email}</strong>. Valid for 10 minutes.
        </div>
        <Field label="Verification Code" value={code} onChange={setCode} placeholder="123456" />
        <ErrBox msg={error} />
        {info && <div style={{ color: C.success, fontSize: 13, background: C.success + "11", padding: "10px 14px", borderRadius: 8, border: `1px solid ${C.success}33` }}>{info}</div>}
        <Btn onClick={handleVerify} loading={loading}>Verify Email</Btn>
        <button onClick={handleResend} disabled={resending || loading}
          style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: resending ? "wait" : "pointer", padding: 0 }}>
          {resending ? "Sending..." : "Didn't get the code? Resend"}
        </button>
      </div>
    </Card>
  );
}

// ── FIX [C2]: MFA Setup Screen (TOTP — Google Authenticator / Authy) ──────
export function MfaSetupScreen({ signInResult, onSuccess }) {
  const [totpCode, setTotpCode]   = useState("");
  const [totpUri, setTotpUri]     = useState("");
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [step, setStep]           = useState("setup"); // setup | verify

  const startSetup = async () => {
    setLoading(true);
    try {
      const output = await setUpTOTP();
      setTotpUri(output.getSetupUri("TrackFin"));
      setStep("verify");
    } catch (e) {
      setError(mapCognitoError(e));
    } finally {
      setLoading(false);
    }
  };

  const confirmMfa = async () => {
    setLoading(true);
    try {
      await verifyTOTPSetup({ code: totpCode });
      await updateMFAPreference({ totp: { enabled: true, preferred: true } });
      onSuccess();
    } catch (e) {
      setError(mapCognitoError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card subtitle="Secure your account" title="Set up Two-Factor Authentication">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {step === "setup" ? (
          <>
            <div style={{ background: C.gold + "22", border: `1px solid ${C.gold}44`, borderRadius: 10, padding: 14, fontSize: 13, color: C.text, lineHeight: 1.6 }}>
              🔐 <strong>Recommended for financial security.</strong> Set up an authenticator app (Google Authenticator, Authy, or Microsoft Authenticator) to add a second layer of protection to your account.
            </div>
            <Btn onClick={startSetup} loading={loading} variant="gold">Set Up Authenticator App</Btn>
            <button onClick={onSuccess} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer" }}>Skip for now (not recommended)</button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: C.muted }}>Scan this QR code in your authenticator app, then enter the 6-digit code:</div>
            <div style={{ background: C.surface, borderRadius: 10, padding: 16, textAlign: "center" }}>
              {/* In production, render this URI as a QR code using a library like qrcode.react */}
              <div style={{ fontSize: 11, color: C.muted, wordBreak: "break-all" }}>{totpUri}</div>
              <div style={{ fontSize: 11, color: C.accent, marginTop: 8 }}>Use a QR code library to render this as scannable QR</div>
            </div>
            <Field label="6-Digit Code from App" value={totpCode} onChange={setTotpCode} placeholder="123456" />
            <ErrBox msg={error} />
            <Btn onClick={confirmMfa} loading={loading}>Verify & Enable MFA</Btn>
          </>
        )}
      </div>
    </Card>
  );
}

// ── Forgot Password Screen ────────────────────────────────────────────────
export function ForgotPasswordScreen({ onBack }) {
  const [step, setStep]           = useState("request");
  const [email, setEmail]         = useState("");
  const [code, setCode]           = useState("");
  const [newPassword, setPass]    = useState("");
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);

  const handleRequest = async () => {
    setError(""); setLoading(true);
    try {
      await resetPassword({ username: email });
      setStep("reset");
    } catch (e) {
      setError(mapCognitoError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setError(""); setLoading(true);
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      onBack();
    } catch (e) {
      setError(mapCognitoError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card subtitle="Reset your password" title={step === "request" ? "Forgot Password" : "Enter New Password"}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {step === "request" ? (
          <><Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@email.com" /><ErrBox msg={error} /><Btn onClick={handleRequest} loading={loading}>Send Reset Code</Btn></>
        ) : (
          <><Field label="Reset Code" value={code} onChange={setCode} placeholder="123456" /><Field label="New Password" type="password" value={newPassword} onChange={setPass} placeholder="••••••••" /><ErrBox msg={error} /><Btn onClick={handleReset} loading={loading}>Set New Password</Btn></>
        )}
        <button onClick={onBack} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer" }}>← Back to login</button>
      </div>
    </Card>
  );
}

// ── FIX [C3]: Map Cognito error codes to user-friendly messages ───────────
// Never expose internal Cognito messages directly to users
function mapCognitoError(err) {
  const code = err?.name || err?.code || "";
  const map = {
    NotAuthorizedException:        "Incorrect email or password.",
    UserNotFoundException:          "No account found with this email.",
    UsernameExistsException:        "An account with this email already exists.",
    CodeMismatchException:          "Invalid verification code. Please try again.",
    ExpiredCodeException:           "This code has expired. Please request a new one.",
    InvalidPasswordException:       "Password must be at least 8 characters with uppercase, number, and symbol.",
    LimitExceededException:         "Too many attempts. Please wait a few minutes and try again.",
    TooManyRequestsException:       "Too many requests. Please wait a moment.",
    UserNotConfirmedException:      "Please verify your email address before signing in.",
    InvalidParameterException:      "Please check your details and try again.",
    NetworkError:                   "Network error. Please check your connection.",
  };
  return map[code] || "Something went wrong. Please try again.";
}

// ── AuthGate — wraps the entire app ───────────────────────────────────────
export function AuthGate({ children }) {
  const [screen, setScreen]         = useState("login");
  const [pendingEmail, setEmail]     = useState("");
  const [mfaResult, setMfaResult]    = useState(null);
  const [isAuthenticated, setIsAuth] = useState(false);
  const [checking, setChecking]      = useState(true);

  useEffect(() => {
    getCurrentUser().then(() => setIsAuth(true)).catch(() => {}).finally(() => setChecking(false));
  }, []);

  if (checking) return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: C.muted }}>Loading...</div>
    </div>
  );

  if (isAuthenticated) return children({ onSignOut: () => { signOut(); setIsAuth(false); setScreen("login"); } });

  if (screen === "signup")  return <SignupScreen onSuccess={(e) => { setEmail(e); setScreen("verify"); }} onLogin={() => setScreen("login")} onResendCode={(e) => { setEmail(e); setScreen("verify"); }} />;
  if (screen === "verify")  return <VerifyScreen email={pendingEmail} onSuccess={() => setScreen("login")} />;
  if (screen === "mfaSetup") return <MfaSetupScreen signInResult={mfaResult} onSuccess={() => setIsAuth(true)} />;
  if (screen === "forgot")  return <ForgotPasswordScreen onBack={() => setScreen("login")} />;

  return (
    <LoginScreen
      onSuccess={() => setIsAuth(true)}
      onSignup={() => setScreen("signup")}
      onForgot={() => setScreen("forgot")}
      onMfaSetup={(result) => { setMfaResult(result); setScreen("mfaSetup"); }}
      onResendCode={(e) => { setEmail(e); setScreen("verify"); }}
    />
  );
}
